import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import { baseSoundURL } from "../../config";
import { RatingAbsentMessage } from "../../components/Main/Rating/RatingAbsentMessage/RatingAbsentMessage";
import { RatingCard } from "../../components/Main/Rating/RatingCard/RatingCard";
import ratingCardClasses from "../../components/Main/Rating/RatingCard/RatingCard.module.scss";
import { RatingOperatorInfo } from "../../components/Main/Rating/RatingOperatorInfo/RatingOperatorInfo";
import { RatingPrompt } from "../../components/Main/Rating/RatingPrompt/RatingPrompt";
import { RatingQrCode } from "../../components/Main/Rating/RatingQrCode/RatingQrCode";
import { RatingScreensaver } from "../../components/Main/Rating/RatingScreensaver/RatingScreensaver";
import { RatingSubmitButton } from "../../components/Main/Rating/RatingSubmitButton/RatingSubmitButton";
import { RatingThankYou } from "../../components/Main/Rating/RatingThankYou/RatingThankYou";
import { RatingWidget } from "../../components/Main/Rating/RatingWidget/RatingWidget";
import { ModalRatingExtension } from "../../components/Main/Modals/ModalRatingExtension/ModalRatingExtension";
import ratingLayoutClasses from "../../components/Main/Rating/ratingCardLayout.module.scss";
import { useRatingState } from "../../hooks/Rating/useRatingState";
import { useSaveRating } from "../../hooks/Rating/useSaveRating";
import { RatingScreenMode } from "../../types/Rating/RatingTypes";
import { isCompletionPhase } from "../../utils/Rating/isCompletionPhase";
import { isInServicePhaseAllowed } from "../../utils/Rating/isInServicePhaseAllowed";
import { isPhaseScreenConfigured } from "../../utils/Rating/isPhaseScreenConfigured";
import { isScreensaverPhaseAllowed } from "../../utils/Rating/isScreensaverPhaseAllowed";
import { parseRatingParams } from "../../utils/Rating/parseRatingParams";
import {
  clearDismissedRatingSessionKey,
  readDismissedRatingSessionKey,
  writeDismissedRatingSessionKey,
} from "../../utils/Rating/ratingDismissedSessionStorage";
import classes from "./RatingPage.module.scss";

const normalizeMediaUrl = (url: string | null | undefined) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) {
    return url;
  }
  const separator = url.startsWith("/") ? "" : "/";
  return `${baseSoundURL}${separator}${url}`;
};

const RATING_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "touchstart",
  "keydown",
];

const RatingPage = () => {
  const location = useLocation();
  const workplace = useMemo(
    () => parseRatingParams(location.search),
    [location.search],
  );
  const { data: state, isLoading } = useRatingState(workplace);
  const saveRating = useSaveRating();

  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [thankYouRating, setThankYouRating] = useState<number | null>(null);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionDeclined, setExtensionDeclined] = useState(false);
  const [dismissedRatingSessionKey, setDismissedRatingSessionKey] = useState<
    string | undefined
  >();
  const [overlayScreensaver, setOverlayScreensaver] = useState(false);
  const [forcedScreensaver, setForcedScreensaver] = useState(false);
  const lastSessionKeyRef = useRef<string | undefined>();
  const lastScreenModeRef = useRef<RatingScreenMode | undefined>();
  const thankYouUntilRef = useRef<number | null>(null);
  const thankYouSessionKeyRef = useRef<string | undefined>();
  const inServiceLastActivityRef = useRef(Date.now());
  const ratingLastActivityRef = useRef(Date.now());
  const lastTicketWakeKeyRef = useRef<string | null>(null);
  const wasScreensaverVisibleRef = useRef(false);

  const settings = state?.settings;
  const textColor = settings?.textColor ?? "#f5f5f5";
  const cardBlurredBackground = !(settings?.transparentBackground ?? false);
  const screenMode = state?.screenMode;
  const ratingTimeoutSec = settings?.ratingTimeoutSeconds ?? 60;
  const extensionCountdownSec = settings?.extensionCountdownSeconds ?? 10;
  const thankYouSec = settings?.thankYouSeconds ?? extensionCountdownSec;
  const screensaverIdleSec = settings?.screensaverIdleSeconds ?? 120;
  const thankYouDurationMs = thankYouSec * 1000;

  const inServicePhaseAllowed = isInServicePhaseAllowed(
    settings?.inServicePhases,
    state?.ticket?.phase,
  );
  const isInServiceActive =
    state?.screenMode === "IN_SERVICE" && inServicePhaseAllowed;
  const screensaverPhaseAllowed = isScreensaverPhaseAllowed(
    screenMode,
    settings?.screensaverPhases,
    state?.ticket?.phase,
  );
  const ticketCompletionPhase = isCompletionPhase(state?.ticket?.phase);
  const hasScreensaverMedia = (settings?.screensaverMedia?.length ?? 0) > 0;
  const operatorPresent =
    screenMode !== "ABSENT_OPERATOR" && screenMode !== "WORKPLACE_NOT_FOUND";
  const hasNoActiveTicket = !state?.ticket;
  const isIdleWithoutTicket = operatorPresent && hasNoActiveTicket;
  const isScreensaverVisible = useMemo(
    () => {
      if (!hasScreensaverMedia || thankYouRating !== null) {
        return false;
      }
      if (isIdleWithoutTicket) {
        return true;
      }
      return (
        (screensaverPhaseAllowed || forcedScreensaver) &&
        (screenMode === "SCREENSAVER" || overlayScreensaver)
      );
    },
    [
      hasScreensaverMedia,
      isIdleWithoutTicket,
      screensaverPhaseAllowed,
      forcedScreensaver,
      thankYouRating,
      screenMode,
      overlayScreensaver,
    ],
  );
  const ticketPhase = state?.ticket?.phase;
  const ticketPhaseHasScreen = isPhaseScreenConfigured(
    settings?.inServicePhases,
    settings?.ratingPhases,
    ticketPhase,
  );
  const currentSessionKey =
    state?.sessionKey != null ? String(state.sessionKey) : undefined;
  const persistedDismissedSessionKey = useMemo(
    () => (workplace ? readDismissedRatingSessionKey(workplace) : null),
    [workplace],
  );
  const effectiveDismissedSessionKey =
    dismissedRatingSessionKey ?? persistedDismissedSessionKey ?? undefined;
  const isRatingDismissedForSession =
    effectiveDismissedSessionKey != null &&
    currentSessionKey != null &&
    effectiveDismissedSessionKey === currentSessionKey;

  const dismissRatingForCurrentSession = useCallback(() => {
    if (currentSessionKey != null && workplace) {
      writeDismissedRatingSessionKey(workplace, currentSessionKey);
      setDismissedRatingSessionKey(currentSessionKey);
    }
    setExtensionDeclined(true);
  }, [currentSessionKey, workplace]);

  useEffect(() => {
    if (!workplace || !currentSessionKey) {
      return;
    }
    const storedSessionKey = readDismissedRatingSessionKey(workplace);
    if (storedSessionKey === currentSessionKey) {
      setDismissedRatingSessionKey(storedSessionKey);
      setExtensionDeclined(true);
    }
  }, [workplace, currentSessionKey]);

  const activateOverlayScreensaver = useCallback(
    (force = false) => {
      if (!hasScreensaverMedia) {
        return;
      }
      if (force) {
        setForcedScreensaver(true);
        setOverlayScreensaver(true);
        return;
      }
      if (screensaverPhaseAllowed) {
        setOverlayScreensaver(true);
      }
    },
    [hasScreensaverMedia, screensaverPhaseAllowed],
  );

  const resetOverlayScreensaver = useCallback(() => {
    setOverlayScreensaver(false);
    setForcedScreensaver(false);
    inServiceLastActivityRef.current = Date.now();
  }, []);

  const resetRatingActivity = useCallback(() => {
    ratingLastActivityRef.current = Date.now();
  }, []);

  const wakeFromScreensaver = useCallback(() => {
    resetOverlayScreensaver();
    setShowExtensionModal(false);
    resetRatingActivity();
  }, [resetOverlayScreensaver, resetRatingActivity]);

  useEffect(() => {
    const sessionKey = currentSessionKey;
    if (!sessionKey) {
      lastTicketWakeKeyRef.current = null;
      return;
    }
    if (lastSessionKeyRef.current !== sessionKey) {
      const isRealSessionChange =
        lastSessionKeyRef.current != null &&
        lastSessionKeyRef.current !== sessionKey;

      setShowExtensionModal(false);
      if (isRealSessionChange) {
        setExtensionDeclined(false);
        setDismissedRatingSessionKey(undefined);
        if (workplace) {
          clearDismissedRatingSessionKey(workplace);
        }
      }
      if (
        isRealSessionChange &&
        (lastScreenModeRef.current !== "IN_SERVICE" ||
          state?.screenMode !== "AWAITING_RATING")
      ) {
        resetOverlayScreensaver();
      }
      if (isRealSessionChange) {
        resetRatingActivity();
        setSelectedRating(null);
      }
      lastSessionKeyRef.current = sessionKey;
    }
  }, [
    currentSessionKey,
    workplace,
    state?.screenMode,
    resetOverlayScreensaver,
    resetRatingActivity,
  ]);

  useEffect(() => {
    const mode = state?.screenMode;
    const prevMode = lastScreenModeRef.current;
    if (mode && prevMode !== mode) {
      if (mode === "IN_SERVICE" && ticketPhaseHasScreen) {
        wakeFromScreensaver();
      } else if (mode === "AWAITING_RATING" && !isRatingDismissedForSession) {
        wakeFromScreensaver();
      } else if (mode !== "AWAITING_RATING") {
        setShowExtensionModal(false);
      }
    }
    lastScreenModeRef.current = mode;
  }, [
    state?.screenMode,
    ticketPhaseHasScreen,
    isRatingDismissedForSession,
    wakeFromScreensaver,
  ]);

  useEffect(() => {
    wasScreensaverVisibleRef.current = isScreensaverVisible;
  }, [isScreensaverVisible]);

  useEffect(() => {
    const ticketWakeKey =
      state?.sessionKey != null && state?.ticket
        ? `${state.sessionKey}|${state.ticket.itemId}|${state.ticket.phase}|${state.ticket.statusTime}`
        : null;
    const prevTicketWakeKey = lastTicketWakeKeyRef.current;
    const wasShowingScreensaver = wasScreensaverVisibleRef.current;

    if (
      ticketWakeKey !== null &&
      ticketWakeKey !== prevTicketWakeKey &&
      (isScreensaverVisible || wasShowingScreensaver) &&
      ticketPhaseHasScreen &&
      (state?.screenMode === "IN_SERVICE" || !isRatingDismissedForSession)
    ) {
      wakeFromScreensaver();
    }

    lastTicketWakeKeyRef.current = ticketWakeKey;
  }, [
    state?.sessionKey,
    state?.screenMode,
    state?.ticket?.itemId,
    state?.ticket?.phase,
    state?.ticket?.statusTime,
    isScreensaverVisible,
    ticketPhaseHasScreen,
    isRatingDismissedForSession,
    wakeFromScreensaver,
  ]);

  useEffect(() => {
    if (state?.screenMode !== "AWAITING_RATING") {
      setSelectedRating(null);
    }
  }, [state?.screenMode]);

  useEffect(() => {
    if (isRatingDismissedForSession && state?.screenMode === "AWAITING_RATING") {
      activateOverlayScreensaver(true);
    }
  }, [isRatingDismissedForSession, state?.screenMode, activateOverlayScreensaver]);

  useEffect(() => {
    if (
      state?.screenMode !== "AWAITING_RATING" ||
      !ticketCompletionPhase ||
      ratingTimeoutSec <= 0 ||
      thankYouRating !== null ||
      extensionDeclined ||
      isRatingDismissedForSession ||
      overlayScreensaver ||
      showExtensionModal
    ) {
      return;
    }

    const markActivity = () => {
      ratingLastActivityRef.current = Date.now();
    };

    RATING_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity),
    );

    const check = () => {
      const elapsed = (Date.now() - ratingLastActivityRef.current) / 1000;
      if (elapsed < ratingTimeoutSec) {
        return;
      }
      if (extensionCountdownSec <= 0) {
        dismissRatingForCurrentSession();
        return;
      }
      setShowExtensionModal(true);
    };

    check();
    const timer = window.setInterval(check, 1000);

    return () => {
      window.clearInterval(timer);
      RATING_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
    };
  }, [
    state?.screenMode,
    state?.sessionKey,
    ticketCompletionPhase,
    ratingTimeoutSec,
    extensionCountdownSec,
    extensionDeclined,
    isRatingDismissedForSession,
    thankYouRating,
    overlayScreensaver,
    showExtensionModal,
    dismissRatingForCurrentSession,
  ]);

  useEffect(() => {
    if (!ticketCompletionPhase) {
      setShowExtensionModal(false);
    }
  }, [ticketCompletionPhase]);

  useEffect(() => {
    if (overlayScreensaver && !screensaverPhaseAllowed && !forcedScreensaver) {
      resetOverlayScreensaver();
    }
  }, [
    overlayScreensaver,
    screensaverPhaseAllowed,
    forcedScreensaver,
    resetOverlayScreensaver,
  ]);

  useEffect(() => {
    if (
      !isInServiceActive ||
      overlayScreensaver ||
      !screensaverPhaseAllowed ||
      screensaverIdleSec <= 0
    ) {
      return;
    }

    inServiceLastActivityRef.current = Date.now();

    const markActivity = () => {
      inServiceLastActivityRef.current = Date.now();
    };

    RATING_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity),
    );

    const timer = window.setInterval(() => {
      const elapsed = (Date.now() - inServiceLastActivityRef.current) / 1000;
      if (elapsed >= screensaverIdleSec) {
        activateOverlayScreensaver();
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
      RATING_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
    };
  }, [
    isInServiceActive,
    screensaverIdleSec,
    overlayScreensaver,
    screensaverPhaseAllowed,
    activateOverlayScreensaver,
  ]);

  useEffect(() => {
    if (thankYouRating === null) {
      return;
    }

    if (isInServiceActive) {
      thankYouUntilRef.current = null;
      thankYouSessionKeyRef.current = undefined;
      setThankYouRating(null);
      return;
    }

    const sessionKey =
      state?.sessionKey != null ? String(state.sessionKey) : undefined;
    if (
      sessionKey &&
      thankYouSessionKeyRef.current != null &&
      sessionKey !== thankYouSessionKeyRef.current
    ) {
      thankYouUntilRef.current = null;
      thankYouSessionKeyRef.current = undefined;
      setThankYouRating(null);
    }
  }, [isInServiceActive, state?.sessionKey, thankYouRating]);

  useEffect(() => {
    if (thankYouRating === null || thankYouSec <= 0) {
      thankYouUntilRef.current = null;
      return;
    }
    if (thankYouUntilRef.current === null) {
      thankYouUntilRef.current = Date.now() + thankYouDurationMs;
    }
    const remaining = thankYouUntilRef.current - Date.now();
    const timer = window.setTimeout(
      () => {
        thankYouUntilRef.current = null;
        thankYouSessionKeyRef.current = undefined;
        setThankYouRating(null);
        activateOverlayScreensaver(true);
      },
      Math.max(remaining, 0),
    );
    return () => window.clearTimeout(timer);
  }, [thankYouRating, activateOverlayScreensaver, thankYouDurationMs, thankYouSec]);

  const handleRatingChange = useCallback((rating: number) => {
    ratingLastActivityRef.current = Date.now();
    setSelectedRating(rating);
  }, []);

  const handleSubmit = async () => {
    if (!workplace || !state || selectedRating === null) {
      return;
    }
    const rating = selectedRating;
    await saveRating.mutateAsync({
      ...workplace,
      rating,
      employeeId: state.operator?.id,
      branchId: state.branchId,
      queueId: state.branchQueueId,
    });
    setShowExtensionModal(false);
    resetOverlayScreensaver();
    setSelectedRating(null);
    if (thankYouSec <= 0) {
      activateOverlayScreensaver(true);
      return;
    }
    thankYouSessionKeyRef.current =
      state.sessionKey != null ? String(state.sessionKey) : undefined;
    thankYouUntilRef.current = Date.now() + thankYouDurationMs;
    setThankYouRating(rating);
  };

  const handleExtensionYes = useCallback(() => {
    setShowExtensionModal(false);
    resetRatingActivity();
  }, [resetRatingActivity]);

  const handleExtensionNo = useCallback(() => {
    setShowExtensionModal(false);
    dismissRatingForCurrentSession();
  }, [dismissRatingForCurrentSession]);

  const handleScreensaverClick = () => {
    if (isRatingDismissedForSession || extensionDeclined || forcedScreensaver) {
      return;
    }
    if (isInServiceActive && ticketPhaseHasScreen) {
      resetOverlayScreensaver();
    }
  };

  const backgroundStyle = useMemo(() => {
    if (!settings) {
      return { backgroundColor: "transparent" };
    }
    if (settings.backgroundType === "IMAGE" && settings.backgroundMediaUrl) {
      return undefined;
    }
    return { backgroundColor: settings.backgroundColor ?? "#1a1a2e" };
  }, [settings]);

  const backgroundImageUrl = useMemo(() => {
    if (!settings || settings.backgroundType !== "IMAGE") {
      return null;
    }
    return normalizeMediaUrl(settings.backgroundMediaUrl);
  }, [settings]);

  if (!workplace) {
    return (
      <div className={classes.page}>
        <div className={classes.content}>
          <p className={classes.error}>
            Укажите параметры branch и queue в адресной строке (cabinet и window
            — число или «-»).
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && !state) {
    return <div className={classes.page} style={backgroundStyle} />;
  }

  if (state?.screenMode === "WORKPLACE_NOT_FOUND") {
    return (
      <div className={classes.page} style={backgroundStyle}>
        <div className={classes.content}>
          <p className={classes.error}>Рабочее место не найдено</p>
        </div>
      </div>
    );
  }

  const showScreensaverOverlay = isScreensaverVisible;

  const showAbsent = state?.screenMode === "ABSENT_OPERATOR" && !showScreensaverOverlay;
  const showRating =
    state?.screenMode === "AWAITING_RATING" &&
    !isRatingDismissedForSession &&
    !extensionDeclined &&
    !overlayScreensaver &&
    !showExtensionModal &&
    thankYouRating === null;
  const showInService =
    isInServiceActive && state.ticket && !overlayScreensaver;

  return (
    <div className={classNames(classes.page)}>
      <div className={classes.background} style={backgroundStyle}>
        {backgroundImageUrl && (
          <div
            className={classes.backgroundImage}
            style={{ backgroundImage: `url("${backgroundImageUrl}")` }}
          />
        )}
      </div>

      {showScreensaverOverlay && (
        <div
          className={classes.screensaverLayer}
          onClick={handleScreensaverClick}
          role="presentation"
        >
          <RatingScreensaver
            media={settings!.screensaverMedia}
            imageSlideSeconds={settings?.screensaverImageSeconds ?? 8}
          />
        </div>
      )}

      {showAbsent && <RatingAbsentMessage textColor={textColor} />}

      {showInService && (
        <div className={ratingLayoutClasses.cardLayer}>
          <RatingCard
            textColor={textColor}
            blurredBackground={cardBlurredBackground}
            className={classNames(
              ratingCardClasses.panel,
              ratingCardClasses.panelCentered,
              classes.inService,
            )}
          >
            {state?.ticket && (
              <p className={classes.ticket}>Талон {state.ticket.ticketNumber}</p>
            )}
            {state?.operator && (
              <p className={classes.operator}>
                Вас обслуживает — {state.operator.fullName}
              </p>
            )}
            <p className={classes.inServicePrompt}>Обслуживание в процессе</p>
          </RatingCard>
        </div>
      )}

      {showRating && (
        <div className={ratingLayoutClasses.cardLayer}>
          <RatingCard
            textColor={textColor}
            blurredBackground={cardBlurredBackground}
            className={ratingCardClasses.panelRating}
          >
            <div className={ratingLayoutClasses.ratingCardContent}>
              <RatingPrompt
                textColor={textColor}
                promptText={settings?.ratingPromptText ?? 'Пожалуйста, оцените качество обслуживания оператором'}
                scaleHint={settings?.ratingScaleHint ?? '(1-плохо, 5-отлично)'}
              />
              {settings?.showOperatorPhoto === true && state.operator && (
                <RatingOperatorInfo
                  textColor={textColor}
                  operatorName={state.operator.fullName}
                  photoUrl={state.operator.photoUrl}
                />
              )}
              <RatingWidget
                ratingType={settings?.ratingType ?? "STARS"}
                value={selectedRating}
                disabled={saveRating.isLoading}
                onChange={handleRatingChange}
              />
              <RatingSubmitButton
                disabled={selectedRating === null}
                loading={saveRating.isLoading}
                onClick={handleSubmit}
              />
              {settings?.showQr && settings.qrUrl && (
                <RatingQrCode
                  url={settings.qrUrl}
                  labelText={settings.qrLabelText}
                />
              )}
            </div>
          </RatingCard>
        </div>
      )}

      {thankYouRating !== null &&
        thankYouSec > 0 &&
        !overlayScreensaver &&
        !showExtensionModal &&
        !isInServiceActive && (
          <RatingThankYou
            rating={thankYouRating}
            textColor={textColor}
            blurredBackground={cardBlurredBackground}
            thankYouText={settings?.thankYouText ?? 'Спасибо за голос!'}
            showQr={settings?.showQr}
            qrUrl={settings?.qrUrl}
            qrLabelText={settings?.qrLabelText}
          />
        )}

      <ModalRatingExtension
        show={showExtensionModal && ticketCompletionPhase && extensionCountdownSec > 0}
        countdownSeconds={extensionCountdownSec}
        textColor={textColor}
        blurredBackground={cardBlurredBackground}
        onYes={handleExtensionYes}
        onNo={handleExtensionNo}
      />
    </div>
  );
};

export default RatingPage;
